import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Switch } from '@/components/ui-kit/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { useIsMobile } from '@/hooks/use-media-query';
import { useDeleteLunchMenu, useUpdateLunchMenu } from '../../hooks/use-manage-lunch';
import { LunchMenu, WEEKDAYS } from '../../types/manage-lunch.types';

export interface MenuTableProps {
  menus: LunchMenu[];
  isLoading?: boolean;
}

const LOADING_ROWS = 4;

/** A change waiting for confirmation: deleting a menu, or turning it off. */
type PendingChange = { kind: 'delete' | 'deactivate'; menu: LunchMenu };

export const MenuTable = ({ menus, isLoading = false }: MenuTableProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { mutateAsync: updateLunchMenu, isPending: isUpdating } = useUpdateLunchMenu();
  const { mutateAsync: deleteLunchMenu, isPending: isDeleting } = useDeleteLunchMenu();
  const [pending, setPending] = useState<PendingChange | null>(null);

  // Turning a menu on is immediate; turning it off or deleting it asks first.
  const handleActiveChange = (menu: LunchMenu, checked: boolean) => {
    if (checked) void updateLunchMenu({ itemId: menu.ItemId, input: { IsActive: true } });
    else setPending({ kind: 'deactivate', menu });
  };
  const handleDelete = (menu: LunchMenu) => setPending({ kind: 'delete', menu });

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'delete') await deleteLunchMenu(pending.menu.ItemId);
      else await updateLunchMenu({ itemId: pending.menu.ItemId, input: { IsActive: false } });
      setPending(null);
    } catch {
      // The error is toasted by the mutation's global handler; the confirmation stays open.
    }
  };

  const isDelete = pending?.kind === 'delete';
  const isConfirming = isDelete ? isDeleting : isUpdating;
  const confirmation = (
    <ConfirmationModal
      open={Boolean(pending)}
      onOpenChange={(open) => !open && setPending(null)}
      title={isDelete ? t('DELETE_LUNCH_MENU') : t('DEACTIVATE_LUNCH_MENU')}
      description={isDelete ? t('MENU_DELETE_WARNING') : t('MENU_DEACTIVATE_WARNING')}
      confirmText={isDelete ? (isDeleting ? t('DELETING') : t('DELETE')) : t('DEACTIVATE')}
      preventAutoClose={isConfirming}
      onConfirm={handleConfirm}
    />
  );

  const menusByDay = WEEKDAYS.map((day) => ({
    day,
    items: menus.filter((menu) => menu.WeekDay === day),
  })).filter((group) => group.items.length > 0);

  if (!isLoading && menusByDay.length === 0) {
    return (
      <Card className="border-none shadow-sm">
        <CardContent className="p-6 text-center text-sm text-muted-foreground md:p-6">
          {t('NO_MENUS_YET')}
        </CardContent>
      </Card>
    );
  }

  // On a phone, per weekday each menu as a card: name with its switch and delete, then the items
  if (isMobile) {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        {confirmation}
        {menusByDay.map((group) => (
          <div key={group.day} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-foreground">{group.day}</h3>
            {group.items.map((menu) => (
              <div
                key={menu.ItemId}
                className="flex flex-col gap-3 rounded-lg border bg-card p-4 text-sm shadow-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-foreground">{menu.Name}</span>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={menu.IsActive}
                      aria-label={t('STATUS')}
                      onCheckedChange={(checked) => handleActiveChange(menu, checked)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(menu)}
                      aria-label={t('DELETE')}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>

                {/* Items rendered as numbered chips */}
                <div className="flex flex-wrap gap-1.5">
                  {menu.MenuItems.map((item, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 rounded border border-border bg-muted/40 px-2 py-0.5 text-xs text-foreground"
                    >
                      <span className="font-semibold text-muted-foreground tabular-nums">
                        {idx + 1}
                      </span>
                      <span>{item}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className="rounded-md border border-border shadow-sm">
      {confirmation}
      <CardContent className="p-0 md:p-0">
        <Table className="min-w-[36rem]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">{t('DAY')}</TableHead>
              <TableHead className="w-48">{t('MENU_NAME')}</TableHead>
              <TableHead>{t('MENU_ITEMS')}</TableHead>
              <TableHead className="w-24 text-center">{t('STATUS')}</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody aria-busy={isLoading}>
            {isLoading &&
              Array.from({ length: LOADING_ROWS }, (_, index) => (
                <TableRow key={`loading-${index}`}>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell className="text-center">
                    <Skeleton className="mx-auto h-5 w-9 rounded-full" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="size-8" />
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading &&
              menusByDay.map((group) =>
                group.items.map((menu, index) => (
                  <TableRow key={menu.ItemId} className="hover:bg-muted/40">
                    {index === 0 && (
                      <TableCell
                        rowSpan={group.items.length}
                        className="align-top font-semibold text-foreground"
                      >
                        {group.day}
                      </TableCell>
                    )}
                    <TableCell className="align-top font-medium text-foreground">
                      {menu.Name}
                    </TableCell>
                    <TableCell className="align-top">
                      {/* Numbered chips matching HR Module Prototype */}
                      <div className="flex flex-wrap gap-1.5 py-0.5">
                        {menu.MenuItems.map((item, itemIdx) => (
                          <span
                            key={itemIdx}
                            className="inline-flex items-center gap-1.5 rounded border border-border bg-card px-2 py-0.5 text-xs font-normal text-foreground shadow-xs"
                          >
                            <span className="font-semibold text-muted-foreground/75 tabular-nums">
                              {itemIdx + 1}
                            </span>
                            <span>{item}</span>
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="align-top text-center">
                      <Switch
                        checked={menu.IsActive}
                        aria-label={t('STATUS')}
                        onCheckedChange={(checked) => handleActiveChange(menu, checked)}
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(menu)}
                        aria-label={t('DELETE')}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
